import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  useMediaQuery
} from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { alpha, makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import TimeWheel from './TimeWheel';
import {
  addTimeInputSeparators,
  formatTimeParts,
  parseTimeInput,
  readTimeParts,
  timePartsToDate,
  timePartsToSeconds
} from '../Utils/time_utils';

const useStyles = makeStyles((theme) => ({
  field: {
    maxWidth: '100%',
    '& .MuiInputBase-root': { minHeight: 44 },
    '& input': { cursor: 'pointer', fontVariantNumeric: 'tabular-nums' },
    '& input:disabled': { cursor: 'default' }
  },
  clock: { color: theme.palette.primary.main, fontSize: 21 },
  container: { [theme.breakpoints.down('xs')]: { alignItems: 'flex-end' } },
  paper: {
    width: 380,
    maxWidth: 'calc(100% - 32px)',
    margin: 16,
    borderTop: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.innerContainer?.background || theme.palette.background.paper,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      maxWidth: 440,
      margin: 0,
      borderRadius: '12px 12px 0 0',
      maxHeight: '100%'
    }
  },
  title: { padding: '18px 24px 8px', '& h2': { fontSize: '1.125rem' } },
  titleRow: { display: 'flex', alignItems: 'baseline', gap: 12 },
  titleText: { flex: 1, minWidth: 0 },
  timePreview: {
    flexShrink: 0,
    color: theme.palette.primary.main,
    fontSize: '1rem',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap'
  },
  content: { padding: '0 24px' },
  preview: {
    padding: '4px 16px 8px',
    marginBottom: 12,
    backgroundColor: alpha(theme.palette.primary.main, 0.07),
    '& input': {
      textAlign: 'center',
      fontSize: '2rem',
      lineHeight: 1.3,
      fontVariantNumeric: 'tabular-nums'
    }
  },
  wheels: { display: 'flex', gap: 8 },
  noTimeout: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 8,
    '& button': { minHeight: 44 }
  },
  actions: {
    padding: '16px 18px',
    gap: 8,
    flexWrap: 'wrap',
    paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
    '& .MuiButton-root': { minHeight: 44, minWidth: 92, margin: 0 },
    '& .MuiButton-root + .MuiButton-root': { marginLeft: 0 }
  },
  cancel: { '&&': { border: 'none', color: theme.palette.text.primary } },
  '@media (max-height: 540px)': {
    title: { paddingTop: 8, paddingBottom: 4 },
    preview: { marginBottom: 8, padding: '0 12px 4px', '& input': { fontSize: '1.5rem' } },
    actions: { paddingTop: 8, paddingBottom: 8 }
  }
}));

function TimePickerDialog({
  value,
  seconds,
  nonZero,
  initialFocusedDate,
  minSeconds,
  maxSeconds,
  renderTimePreview,
  title,
  disabled,
  readOnly,
  onChange,
  onAccept,
  onClose,
  cancelLabel,
  okLabel
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  // Check on the first render so opening on touch does not summon the keyboard.
  const touchInput = useMediaQuery('(pointer: coarse)', { noSsr: true });
  // Snapshot only on opening. Telemetry/readback must not replace an in-progress selection.
  const [draft, setDraft] = useState(() => {
    const current = readTimeParts(value);
    const empty = !value || (nonZero && current?.every((part) => part === 0));
    const parts = (empty ? readTimeParts(initialFocusedDate) : current) || [0, 0, 0];
    return { parts, text: current || empty ? formatTimeParts(parts, seconds) : String(value) };
  });
  const [busy, setBusy] = useState({});
  const [typing, setTyping] = useState(false);
  const input = useRef(null);
  const nextCaret = useRef(null);
  useLayoutEffect(() => {
    if (nextCaret.current !== null) {
      input.current.setSelectionRange(nextCaret.current, nextCaret.current);
      nextCaret.current = null;
    }
  }, [draft]);
  const parsed = parseTimeInput(draft.text, seconds);
  const inRange =
    parsed &&
    (minSeconds === undefined || timePartsToSeconds(parsed) >= minSeconds) &&
    (maxSeconds === undefined || timePartsToSeconds(parsed) <= maxSeconds);
  const locked = disabled || readOnly;
  const moving = Object.values(busy).some(Boolean);
  const noTimeout = nonZero && parsed?.every((part) => part === 0);

  const accept = () => {
    if (locked || moving || !inRange) return;
    const date = timePartsToDate(parsed);
    onChange?.(date);
    onAccept?.(date);
    onClose();
  };

  const changePart = (index, value) => {
    if (locked || typing) return;
    setDraft((previous) => {
      const parts = [...previous.parts];
      parts[index] = value;
      return { parts, text: formatTimeParts(parts, seconds) };
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{
        'aria-label': title,
        onKeyDown: (event) => {
          // A portaled picker must not submit the surrounding action dialog.
          if (event.key === 'Enter') event.stopPropagation();
        }
      }}
      maxWidth={false}
      classes={{ container: classes.container, paper: classes.paper }}
    >
      <DialogTitle className={classes.title}>
        <span className={classes.titleRow}>
          <span className={classes.titleText}>{title}</span>
          {renderTimePreview && (
            <span className={classes.timePreview}>
              {parsed && renderTimePreview(timePartsToDate(parsed))}
            </span>
          )}
        </span>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.preview}>
          <TextField
            autoFocus={!touchInput}
            inputRef={input}
            fullWidth
            value={draft.text}
            disabled={locked}
            inputProps={{
              'aria-label': t('time_picker_enter'),
              'aria-invalid': !inRange,
              inputMode: 'numeric',
              autoComplete: 'off',
              maxLength: seconds ? 8 : 5
            }}
            onFocus={(event) => {
              event.target.select();
            }}
            onChange={(event) => {
              const { value, selectionStart, selectionEnd } = event.target;
              const deleting = event.nativeEvent.inputType?.startsWith('delete');
              const text =
                !deleting && selectionStart === value.length && selectionEnd === value.length
                  ? addTimeInputSeparators(value, seconds)
                  : value;
              nextCaret.current = text !== value ? text.length : selectionStart;
              setTyping(true);
              setDraft((previous) => ({
                parts: parseTimeInput(text, seconds) || previous.parts,
                text
              }));
            }}
            onBlur={() => {
              setTyping(false);
              if (parsed) setDraft({ parts: parsed, text: formatTimeParts(parsed, seconds) });
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                accept();
              }
            }}
          />
        </div>
        <div className={classes.wheels}>
          {['hours', 'minutes', 'seconds'].slice(0, seconds ? 3 : 2).map((unit, index) => (
            <TimeWheel
              key={unit}
              label={t(`time_picker_${unit}`)}
              value={draft.parts[index]}
              max={index === 0 ? 23 : 59}
              disabled={locked || typing}
              onChange={(next) => changePart(index, next)}
              onBusyChange={(isBusy) =>
                setBusy((previous) =>
                  previous[unit] === isBusy ? previous : { ...previous, [unit]: isBusy }
                )
              }
            />
          ))}
        </div>
        {nonZero && (
          <div className={classes.noTimeout}>
            <Button
              color="primary"
              variant={noTimeout ? 'contained' : 'outlined'}
              disabled={locked || moving}
              aria-pressed={Boolean(noTimeout)}
              onClick={() =>
                setDraft({ parts: [0, 0, 0], text: formatTimeParts([0, 0, 0], seconds) })
              }
            >
              {t('no_overwr_timeout')}
            </Button>
          </div>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button className={classes.cancel} onClick={onClose}>
          {cancelLabel || t('cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disableElevation
          disabled={locked || moving || !inRange}
          onClick={accept}
        >
          {okLabel || t('time_picker_use')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function WheelTimePicker({
  value,
  seconds = false,
  nonZero = false,
  initialFocusedDate,
  minSeconds,
  maxSeconds,
  renderTimePreview,
  onChange,
  onAccept,
  onOpen,
  onClose,
  disabled = false,
  readOnly = false,
  label,
  dialogTitle,
  name,
  cancelLabel,
  okLabel,
  InputProps,
  inputProps,
  ...others
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const parts = readTimeParts(value);
  const noTimeout = nonZero && parts?.every((part) => part === 0);
  const title =
    label || (nonZero ? t('event_duration') : t(name || 'time', { defaultValue: t('time') }));
  const openPicker = () => {
    if (disabled || readOnly) return;
    setOpen(true);
    onOpen?.();
  };
  const closePicker = () => {
    setOpen(false);
    onClose?.();
  };

  return (
    <>
      <TextField
        margin="normal"
        {...others}
        className={[classes.field, others.className].filter(Boolean).join(' ')}
        label={label}
        name={name}
        value={noTimeout ? '' : parts ? formatTimeParts(parts, seconds) : value || ''}
        disabled={disabled}
        onClick={openPicker}
        onKeyDown={(event) => {
          if (['Enter', ' '].includes(event.key)) {
            event.preventDefault();
            openPicker();
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <AccessTimeIcon className={classes.clock} />
            </InputAdornment>
          ),
          ...InputProps,
          readOnly: true
        }}
        inputProps={{
          'aria-label': title,
          ...inputProps,
          role: 'button',
          'aria-haspopup': 'dialog',
          'aria-expanded': open,
          'aria-disabled': disabled || readOnly || undefined
        }}
      />
      {open && (
        <TimePickerDialog
          value={value}
          seconds={seconds}
          nonZero={nonZero}
          initialFocusedDate={initialFocusedDate}
          minSeconds={minSeconds}
          maxSeconds={maxSeconds}
          renderTimePreview={renderTimePreview}
          title={dialogTitle || title}
          disabled={disabled}
          readOnly={readOnly}
          onChange={onChange}
          onAccept={onAccept}
          onClose={closePicker}
          cancelLabel={cancelLabel}
          okLabel={okLabel}
        />
      )}
    </>
  );
}
